import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

type GalleryCategory = "amenities" | "architecture" | "interior" | "landscape";

interface GalleryImage {
  /** Full-size image shown in the lightbox. */
  src: string;
  /** WebP variant of `src`, when the image is served from our own origin. */
  srcWebp?: string;
  /** Smaller variant used in the grid; falls back to `src` when absent. */
  thumb?: string;
  /** WebP variant of `thumb`. */
  thumbWebp?: string;
  title: string;
  description: string;
  category: GalleryCategory;
  /** Recently photographed on site — surfaced first and under the "Latest" filter. */
  latest?: boolean;
}

const CATEGORY_LABELS: Record<GalleryCategory, string> = {
  amenities: "Amenities",
  architecture: "Architecture",
  interior: "Interior",
  landscape: "Landscape",
};

const GallerySection = () => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>("latest");

  // Photographed on site, August 2026. Served from our own origin, not an
  // external CDN, so the gallery keeps working independently of any vendor.
  const galleryImages: GalleryImage[] = [
    {
      src: "/swanlake-entrance-signage.jpg",
      srcWebp: "/swanlake-entrance-signage.webp",
      thumb: "/swanlake-entrance-signage-thumb.jpg",
      thumbWebp: "/swanlake-entrance-signage-thumb.webp",
      title: "Entrance Name Board in Bloom",
      description:
        "The Purva Swanlake name board at the main gate, framed by a bed of white rain lilies in full flower.",
      category: "architecture",
      latest: true,
    },
    {
      src: "/swanlake-aerial-complex.jpg",
      srcWebp: "/swanlake-aerial-complex.webp",
      thumb: "/swanlake-aerial-complex-thumb.jpg",
      thumbWebp: "/swanlake-aerial-complex-thumb.webp",
      title: "The Whole Community from the Air",
      description:
        "All the towers together, with the backwaters and Kelambakkam stretching out behind them.",
      category: "landscape",
      latest: true,
    },
    {
      src: "/swanlake-aerial-pool.jpg",
      srcWebp: "/swanlake-aerial-pool.webp",
      thumb: "/swanlake-aerial-pool-thumb.jpg",
      thumbWebp: "/swanlake-aerial-pool-thumb.webp",
      title: "Pool and Deck from Above",
      description:
        "The pool, the shaded gazebo and the surrounding deck seen from one of the upper floors.",
      category: "amenities",
      latest: true,
    },
    {
      src: "/swanlake-pool-day.jpg",
      srcWebp: "/swanlake-pool-day.webp",
      thumb: "/swanlake-pool-day-thumb.jpg",
      thumbWebp: "/swanlake-pool-day-thumb.webp",
      title: "Swimming Pool on a Clear Day",
      description:
        "The main pool and the shallow children's pool between the towers, with the patterned deck and trimmed topiary along the edge.",
      category: "amenities",
      latest: true,
    },
    {
      src: "/swanlake-entrance-night.jpg",
      srcWebp: "/swanlake-entrance-night.webp",
      thumb: "/swanlake-entrance-night-thumb.jpg",
      thumbWebp: "/swanlake-entrance-night-thumb.webp",
      title: "Main Entrance, Lit for the Evening",
      description:
        "The entrance arch and security cabin under festive lighting — how the community greets residents after dark.",
      category: "architecture",
      latest: true,
    },
    {
      src: "/swanlake-playground.jpg",
      srcWebp: "/swanlake-playground.webp",
      thumb: "/swanlake-playground-thumb.jpg",
      thumbWebp: "/swanlake-playground-thumb.webp",
      title: "Children's Play Area",
      description:
        "Slides, climbers and swings set on soft sand, shaded by trees along the compound wall.",
      category: "amenities",
      latest: true,
    },
    {
      src: "/swanlake-walkway.jpg",
      srcWebp: "/swanlake-walkway.webp",
      thumb: "/swanlake-walkway-thumb.jpg",
      thumbWebp: "/swanlake-walkway-thumb.webp",
      title: "Covered Walkway and Benches",
      description:
        "The roofed walkway running along the lawn, with red benches for a shaded sit-out between the towers.",
      category: "amenities",
      latest: true,
    },
    {
      src: "/swanlake-pool-fountain.jpg",
      srcWebp: "/swanlake-pool-fountain.webp",
      thumb: "/swanlake-pool-fountain-thumb.jpg",
      thumbWebp: "/swanlake-pool-fountain-thumb.webp",
      title: "Pool Fountain and Poolside Greenery",
      description:
        "The fountain at the far end of the pool, flanked by clipped hedges and poolside seating.",
      category: "amenities",
      latest: true,
    },
    {
      src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/vDmtVuOJusreBjNf.jpg",
      title: "Aerial View with Lake Landscape",
      description:
        "Aerial view of Purva Swanlake with the towers set against the lake and the surrounding landscape.",
      category: "landscape",
    },
    {
      src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/SsWZVEoTspuASnwV.jpg",
      title: "Towers with Pool & Landscaping",
      description:
        "Panoramic view of the residential towers with the swimming pool and lush green landscaping.",
      category: "architecture",
    },
    {
      src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/fFMvGjBUobluyXah.jpg",
      title: "Modern Residential Complex",
      description:
        "Contemporary architecture of Purva Swanlake with multiple towers and the pool area.",
      category: "architecture",
    },
    {
      src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/dzAxWyFfJghAsRXu.jpg",
      title: "Premium Tower Design",
      description: "Modern facade and premium residential design across the towers.",
      category: "architecture",
    },
    {
      src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/mkaoCytDPHTsOTLK.jpg",
      title: "Towers & Amenities",
      description: "Residential towers overlooking community amenities and landscaped gardens.",
      category: "architecture",
    },
    {
      src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/qTZrZjyKmkAjDJBu.jpg",
      title: "Community Exterior View",
      description: "Exterior view of the Purva Swanlake complex with towers and green spaces.",
      category: "landscape",
    },
    {
      src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/NpgBRClDtIcbhtHT.jpg",
      title: "Modern Bedroom Design",
      description: "Spacious and elegantly designed bedroom with premium furnishings.",
      category: "interior",
    },
    {
      src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/dZGRDQfEpuZpcWun.jpg",
      title: "Living Room",
      description: "Bright and airy living space with modern finishes.",
      category: "interior",
    },
    {
      src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/ngMwmgWpLRGVpxWr.jpg",
      title: "Swimming Pool & Amenities",
      description: "The swimming pool with the surrounding landscaping and deck.",
      category: "amenities",
    },
    {
      src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/kcIIAzsIvwMhsBSz.jpg",
      title: "Exterior View",
      description: "Exterior view of the residential complex and its green surroundings.",
      category: "landscape",
    },
    {
      src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/fqfVLDnoKHAbLMca.jpg",
      title: "Complex Overview",
      description: "Panoramic view of the entire Purva Swanlake complex.",
      category: "architecture",
    },
    {
      src: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/ZKNRuqQycsfhQKCO.jpg",
      title: "Landscaping & Gardens",
      description: "Lush green spaces and landscaped gardens throughout the property.",
      category: "landscape",
    },
  ];

  const filters = [
    { id: "latest", label: "Latest" },
    { id: "all", label: "All" },
    { id: "amenities", label: CATEGORY_LABELS.amenities },
    { id: "architecture", label: CATEGORY_LABELS.architecture },
    { id: "interior", label: CATEGORY_LABELS.interior },
    { id: "landscape", label: CATEGORY_LABELS.landscape },
  ];

  const visibleImages = useMemo(() => {
    if (activeFilter === "all") return galleryImages;
    if (activeFilter === "latest") return galleryImages.filter((image) => image.latest);
    return galleryImages.filter((image) => image.category === activeFilter);
    // galleryImages is a stable literal defined in this component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter]);

  const closeLightbox = useCallback(() => setSelectedImage(null), []);

  const handlePrevious = useCallback(() => {
    setSelectedImage((current) =>
      current === null ? null : current === 0 ? visibleImages.length - 1 : current - 1
    );
  }, [visibleImages.length]);

  const handleNext = useCallback(() => {
    setSelectedImage((current) =>
      current === null ? null : current === visibleImages.length - 1 ? 0 : current + 1
    );
  }, [visibleImages.length]);

  // The lightbox tells people to use the arrow keys and Esc, so it has to listen.
  useEffect(() => {
    if (selectedImage === null) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeLightbox();
      if (event.key === "ArrowLeft") handlePrevious();
      if (event.key === "ArrowRight") handleNext();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedImage, closeLightbox, handlePrevious, handleNext]);

  // Tiles open the lightbox, so they behave like buttons for keyboard users too.
  const openOnKey = (event: ReactKeyboardEvent, index: number) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setSelectedImage(index);
    }
  };

  const renderImage = (
    image: GalleryImage,
    variant: "thumb" | "full",
    className: string,
    loading: "lazy" | "eager" = "lazy"
  ) => {
    const jpg = variant === "thumb" ? image.thumb ?? image.src : image.src;
    const webp = variant === "thumb" ? image.thumbWebp ?? image.srcWebp : image.srcWebp;

    return (
      <picture className={variant === "thumb" ? "block w-full h-full" : "block w-full"}>
        {webp && <source srcSet={webp} type="image/webp" />}
        <img
          src={jpg}
          alt={image.title}
          loading={loading}
          decoding="async"
          className={className}
        />
      </picture>
    );
  };

  const featured = visibleImages[0];
  const activeImage = selectedImage !== null ? visibleImages[selectedImage] : null;

  return (
    <section
      id="gallery"
      className="py-20 sm:py-32 bg-gradient-to-b from-white via-slate-50 to-white"
    >
      <div className="section-container">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-5xl sm:text-6xl font-bold text-slate-900 mb-6 tracking-tight">
            Gallery
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto font-light">
            Purva Swanlake as it looks today — from the air, at the gate, around the pool,
            the play area and the walkways, photographed across the community.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {filters.map((filter) => (
            <button
              key={filter.id}
              type="button"
              onClick={() => {
                setActiveFilter(filter.id);
                setSelectedImage(null);
              }}
              aria-pressed={activeFilter === filter.id}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                activeFilter === filter.id
                  ? "bg-blue-600 text-white shadow-lg scale-105"
                  : "bg-white text-slate-700 border border-slate-200 hover:border-blue-400 hover:shadow-md"
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Gallery Grid - Masonry Layout with a featured first tile */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {featured && (
            <div
              className="lg:col-span-2 lg:row-span-2 group relative overflow-hidden rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer h-96 lg:h-full hover:-translate-y-1 focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-500"
              role="button"
              tabIndex={0}
              aria-label={`Open ${featured.title}`}
              onClick={() => setSelectedImage(0)}
              onKeyDown={(event) => openOnKey(event, 0)}
            >
              {renderImage(
                featured,
                "thumb",
                "w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-8">
                <span className="inline-block bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  {CATEGORY_LABELS[featured.category]}
                </span>
                <h3 className="text-white text-3xl font-bold mb-3">{featured.title}</h3>
                <p className="text-white/90 text-base leading-relaxed">{featured.description}</p>
              </div>
            </div>
          )}

          {visibleImages.slice(1).map((image, index) => (
            <div
              key={image.src}
              className="group relative overflow-hidden rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer h-64 sm:h-80 hover:-translate-y-1 focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-500"
              role="button"
              tabIndex={0}
              aria-label={`Open ${image.title}`}
              onClick={() => setSelectedImage(index + 1)}
              onKeyDown={(event) => openOnKey(event, index + 1)}
            >
              {renderImage(
                image,
                "thumb",
                "w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                <span className="inline-block bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-2 w-fit">
                  {CATEGORY_LABELS[image.category]}
                </span>
                <h3 className="text-white text-xl font-bold mb-2">{image.title}</h3>
                <p className="text-white/90 text-sm leading-relaxed">{image.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Image Counter */}
        <div className="text-center mb-12">
          <p className="text-slate-600 text-lg font-light">
            Showing{" "}
            <span className="font-bold text-slate-900">{visibleImages.length} images</span> of our
            community
          </p>
        </div>

        {/* Lightbox Modal */}
        {activeImage && (
          <div
            className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-label={activeImage.title}
          >
            {/* Close Button */}
            <button
              type="button"
              onClick={closeLightbox}
              className="absolute top-6 right-6 text-white hover:bg-white/20 p-3 rounded-full transition-colors z-10"
              title="Close (Esc)"
              aria-label="Close gallery"
            >
              <X className="w-8 h-8" />
            </button>

            {/* Image Container */}
            <div className="relative max-w-6xl w-full">
              {renderImage(
                activeImage,
                "full",
                "w-full h-auto max-h-[70vh] object-contain rounded-2xl shadow-2xl",
                "eager"
              )}

              {/* Image Info */}
              <div className="mt-8 text-center">
                <span className="inline-block bg-blue-500 text-white text-xs font-bold px-4 py-2 rounded-full mb-4">
                  {CATEGORY_LABELS[activeImage.category]}
                </span>
                <h3 className="text-3xl font-bold text-white mb-3">{activeImage.title}</h3>
                <p className="text-white/80 text-lg max-w-3xl mx-auto">
                  {activeImage.description}
                </p>
                <p className="text-white/60 text-sm mt-6">
                  {(selectedImage ?? 0) + 1} / {visibleImages.length}
                </p>
              </div>

              {/* Navigation Buttons */}
              <button
                type="button"
                onClick={handlePrevious}
                className="absolute left-4 top-1/3 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white p-4 rounded-full transition-colors backdrop-blur-sm"
                title="Previous (←)"
                aria-label="Previous image"
              >
                <ChevronLeft className="w-7 h-7" />
              </button>
              <button
                type="button"
                onClick={handleNext}
                className="absolute right-4 top-1/3 -translate-y-1/2 bg-white/20 hover:bg-white/40 text-white p-4 rounded-full transition-colors backdrop-blur-sm"
                title="Next (→)"
                aria-label="Next image"
              >
                <ChevronRight className="w-7 h-7" />
              </button>

              {/* Keyboard Navigation Hint */}
              <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-white/50 text-xs">
                Use arrow keys to navigate • Press Esc to close
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default GallerySection;
